import React, { useState, useEffect } from 'react';
import './SciencePaperSearch.css';

const SciencePaperSearch = ({ onTimestampClick }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searched, setSearched] = useState(false);
    const [selectedTimestamp, setSelectedTimestamp] = useState(null);
    const [lastAppliedTimestamp, setLastAppliedTimestamp] = useState(null);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [timestampError, setTimestampError] = useState(null);
    // Track which event is expanded to show details
    const [expandedEventIndex, setExpandedEventIndex] = useState(null);
    // Track whether all timestamps are shown
    const [showAllTimestamps, setShowAllTimestamps] = useState(false);
    // Track last successfully parsed date for use as fallback when parsing time-only formats
    const [lastParsedDate, setLastParsedDate] = useState(null);
    // Track which subevent is expanded to show its details
    const [expandedSubeventIndex, setExpandedSubeventIndex] = useState(null);

    // Load persisted state from localStorage on mount
    useEffect(() => {
        try {
            const savedState = localStorage.getItem('timestampQueryState');
            if (savedState) {
                const parsed = JSON.parse(savedState);
                setQuery(parsed.query || '');
                setResults(parsed.results || []);
                setSearched(parsed.searched || false);
                setLastAppliedTimestamp(parsed.lastAppliedTimestamp || null);
                // Parse and store the last applied timestamp as a Date object
                if (parsed.lastAppliedTimestamp) {
                    const parsedDate = parseTimestamp(parsed.lastAppliedTimestamp);
                    if (parsedDate) {
                        setLastParsedDate(parsedDate);
                    }
                }
            }
        } catch (e) {
            console.error('Error loading timestamp query state:', e);
        }
    }, []);

    // Save state to localStorage whenever it changes
    useEffect(() => {
        try {
            const stateToSave = {
                query,
                results,
                searched,
                lastAppliedTimestamp
            };
            localStorage.setItem('timestampQueryState', JSON.stringify(stateToSave));
        } catch (e) {
            console.error('Error saving timestamp query state:', e);
        }
    }, [query, results, searched, lastAppliedTimestamp]);

    // Timer for elapsed seconds during loading
    useEffect(() => {
        let intervalId;
        if (loading) {
            setElapsedSeconds(0);
            intervalId = setInterval(() => {
                setElapsedSeconds(prev => prev + 1);
            }, 1000);
        }
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [loading]);

    const handleSearch = async () => {
        if (!query.trim()) {
            setError('Please enter a search query');
            return;
        }

        setLoading(true);
        setError(null);
        setSearched(true);

        try {
            const response = await fetch('/api/query_timestamps', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ query }),
            });

            if (!response.ok) {
                throw new Error('Failed to fetch results');
            }

            const data = await response.json();

            if (data.error) {
                setError(data.error);
                setResults([]);
            } else {
                // Sort results by relevance score (matchScore) in descending order
                const sortedResults = (data.results || []).sort((a, b) => {
                    return (b.matchScore || 0) - (a.matchScore || 0);
                });
                console.log('Search results received:', sortedResults.length);
                if (sortedResults.length > 0) {
                    console.log('First result allTimestamps:', sortedResults[0].allTimestamps);
                }
                setResults(sortedResults);
            }
        } catch (err) {
            setError(err.message || 'An error occurred while searching');
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            // Optional: Show a brief "Copied!" message
            console.log('Copied to clipboard:', text);
        });
    };

    const parseTimestamp = (timestampStr, fallbackDate = null) => {
        // Remove '~', 'UT', 'UTC', and extra spaces
        let cleanStr = timestampStr.replace(/~/g, '').replace(/\s*(UT|UTC)\s*/gi, ' ').trim();
        
        // Common month names
        const months = {
            'january': 0, 'february': 1, 'march': 2, 'april': 3,
            'may': 4, 'june': 5, 'july': 6, 'august': 7,
            'september': 8, 'october': 9, 'november': 10, 'december': 11
        };
        
        try {
            // Pattern: "DD Month YYYY, HH:MM:SS" or "DD Month YYYY, HH:MM"
            const pattern1 = /(\d{1,2})\s+(\w+)\s+(\d{4}),?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?/;
            const match1 = cleanStr.match(pattern1);
            
            if (match1) {
                const day = parseInt(match1[1]);
                const monthName = match1[2].toLowerCase();
                const year = parseInt(match1[3]);
                const hour = parseInt(match1[4]);
                const minute = parseInt(match1[5]);
                const second = match1[6] ? parseInt(match1[6]) : 0;
                
                const month = months[monthName];
                if (month !== undefined) {
                    return new Date(Date.UTC(year, month, day, hour, minute, second));
                }
            }
            
            // Pattern: "YYYY-MM-DD HH:MM:SS" or "YYYY-MM-DD HH:MM" or ISO format
            const pattern2 = /(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/;
            const match2 = cleanStr.match(pattern2);
            
            if (match2) {
                const year = parseInt(match2[1]);
                const month = parseInt(match2[2]) - 1;
                const day = parseInt(match2[3]);
                const hour = parseInt(match2[4]);
                const minute = parseInt(match2[5]);
                const second = match2[6] ? parseInt(match2[6]) : 0;
                
                return new Date(Date.UTC(year, month, day, hour, minute, second));
            }
            
            // Pattern: Time only "HH:MM:SS" or "HH:MM" - use fallback date if provided
            const pattern3 = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
            const match3 = cleanStr.match(pattern3);
            
            if (match3 && fallbackDate) {
                const hour = parseInt(match3[1]);
                const minute = parseInt(match3[2]);
                const second = match3[3] ? parseInt(match3[3]) : 0;
                
                return new Date(Date.UTC(
                    fallbackDate.getUTCFullYear(),
                    fallbackDate.getUTCMonth(),
                    fallbackDate.getUTCDate(),
                    hour,
                    minute,
                    second
                ));
            }
            
            // Pattern: Date without year "MM-DD HH:MM:SS" - use fallback year if provided
            const pattern4 = /^(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?$/;
            const match4 = cleanStr.match(pattern4);
            
            if (match4 && fallbackDate) {
                const month = parseInt(match4[1]) - 1;
                const day = parseInt(match4[2]);
                const hour = parseInt(match4[3]);
                const minute = parseInt(match4[4]);
                const second = match4[5] ? parseInt(match4[5]) : 0;
                
                return new Date(Date.UTC(
                    fallbackDate.getUTCFullYear(),
                    month,
                    day,
                    hour,
                    minute,
                    second
                ));
            }
            
            // Pattern: "Month DD HH:MM:SS" (e.g., "May 20 03:04:03") - use fallback year if provided
            const pattern5 = /^(\w+)\s+(\d{1,2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
            const match5 = cleanStr.match(pattern5);
            
            if (match5 && fallbackDate) {
                const monthName = match5[1].toLowerCase();
                const day = parseInt(match5[2]);
                const hour = parseInt(match5[3]);
                const minute = parseInt(match5[4]);
                const second = match5[5] ? parseInt(match5[5]) : 0;
                
                const month = months[monthName];
                if (month !== undefined) {
                    return new Date(Date.UTC(
                        fallbackDate.getUTCFullYear(),
                        month,
                        day,
                        hour,
                        minute,
                        second
                    ));
                }
            }
            
            // If we can't parse, return null
            return null;
        } catch (e) {
            console.error('Error parsing timestamp:', e);
            return null;
        }
    };

    const handleEventClick = (index) => {
        // Toggle expansion: if already expanded, collapse; otherwise expand
        const isExpanding = expandedEventIndex !== index;
        setExpandedEventIndex(isExpanding ? index : null);
        if (isExpanding) {
            setShowAllTimestamps(false); // Reset show all timestamps
            setExpandedSubeventIndex(null); // Reset subevent expansion
        }
    };

    const handleSubeventClick = (index) => {
        // Toggle subevent expansion
        setExpandedSubeventIndex(expandedSubeventIndex === index ? null : index);
    };

    const handleApplyTimestampToCalendar = (timestampStr) => {
        // Use lastParsedDate as fallback date for time-only formats
        const parsedDate = parseTimestamp(timestampStr, lastParsedDate);
        
        if (parsedDate) {
            console.log('Applying timestamp to calendar:', parsedDate);
            
            // Check if date is before MMS launch (September 1, 2015)
            const mmsLaunchDate = new Date('2015-09-01T00:00:00Z');
            if (parsedDate < mmsLaunchDate) {
                const displayStr = parsedDate.toISOString().replace('.000Z', 'Z');
                setTimestampError({
                    timestamp: displayStr,
                    message: 'This timestamp is before the MMS launch date (September 1, 2015). MMS data is not available for this date.'
                });
                // Clear error message after 4 seconds
                setTimeout(() => setTimestampError(null), 3000);
                return;
            }
            
            // Display the full parsed timestamp
            const displayStr = parsedDate.toISOString().replace('.000Z', 'Z');
            setSelectedTimestamp(displayStr);
            setLastAppliedTimestamp(displayStr); // Persist the last applied timestamp
            setLastParsedDate(parsedDate); // Store the parsed date for future fallback
            setTimestampError(null); // Clear any previous errors
            if (onTimestampClick) {
                onTimestampClick(parsedDate);
            }
            // Clear success message after 2 seconds
            setTimeout(() => setSelectedTimestamp(null), 3000);
        } else {
            console.warn('Could not parse timestamp:', timestampStr);
            alert('Could not parse this timestamp format. Please try copying it and entering manually.');
        }
    };

    const handleCurrentSelectionEdit = (e) => {
        const newValue = e.target.value;
        setLastAppliedTimestamp(newValue);
    };

    const handleCurrentSelectionBlur = () => {
        // Validate and normalize the format (but don't apply to calendar)
        if (lastAppliedTimestamp) {
            const parsedDate = parseTimestamp(lastAppliedTimestamp);
            if (parsedDate) {
                const displayStr = parsedDate.toISOString().replace('.000Z', 'Z');
                setLastAppliedTimestamp(displayStr);
            }
        }
    };

    const handleApplyCurrentSelection = () => {
        // Validate and apply the edited timestamp to the calendar
        if (lastAppliedTimestamp) {
            // Use lastParsedDate as fallback for time-only formats
            const parsedDate = parseTimestamp(lastAppliedTimestamp, lastParsedDate);
            if (parsedDate) {
                // Check if date is before MMS launch (September 1, 2015)
                const mmsLaunchDate = new Date('2015-09-01T00:00:00Z');
                if (parsedDate < mmsLaunchDate) {
                    const displayStr = parsedDate.toISOString().replace('.000Z', 'Z');
                    setTimestampError({
                        timestamp: displayStr,
                        message: 'This timestamp is before the MMS launch date (September 1, 2015). MMS data is not available for this date.'
                    });
                    // Clear error message after 4 seconds
                    setTimeout(() => setTimestampError(null), 4000);
                    return;
                }
                
                const displayStr = parsedDate.toISOString().replace('.000Z', 'Z');
                setLastAppliedTimestamp(displayStr);
                setSelectedTimestamp(displayStr);
                setLastParsedDate(parsedDate); // Store the parsed date for future fallback
                setTimestampError(null); // Clear any previous errors
                if (onTimestampClick) {
                    onTimestampClick(parsedDate);
                }
                // Show success message for 2 seconds
                setTimeout(() => setSelectedTimestamp(null), 2000);
            } else {
                alert('Invalid timestamp format. Please use ISO format (YYYY-MM-DDTHH:MM:SSZ)');
            }
        }
    };

    const handleCurrentSelectionKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyCurrentSelection();
        }
    };

    return (
        <div className="timestamp-query-container">
            <p className="timestamp-query-description">
                Search for MMS event timestamps and DOIs from research papers
            </p>

            {lastAppliedTimestamp && (
                <div className="timestamp-last-applied">
                    <span className="timestamp-last-applied-label">Current Selection:</span>
                    <input
                        type="text"
                        className="timestamp-last-applied-input"
                        value={lastAppliedTimestamp}
                        onChange={handleCurrentSelectionEdit}
                        onBlur={handleCurrentSelectionBlur}
                        onKeyPress={handleCurrentSelectionKeyPress}
                        placeholder="YYYY-MM-DDTHH:MM:SSZ"
                        title="Edit this timestamp directly. Press Enter or click the Apply button to add to Selected Time Span."
                    />
                    <button
                        className="timestamp-apply-button"
                        onClick={handleApplyCurrentSelection}
                        title="Apply this timestamp to Selected Time Span"
                    >
                        Apply to Calendar
                    </button>
                </div>
            )}

            <div className="timestamp-search-box">
                <div className="timestamp-query-input-wrapper">
                    <textarea
                        className="timestamp-query-input"
                        placeholder="e.g., reconnection September 2015, Kelvin-Helmholtz, dipolarization front..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyPress={(e) => {
                            // Allow Enter to search, unless Shift+Enter (for new line)
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSearch();
                            }
                        }}
                        disabled={loading}
                        rows={2}
                        title="Tip: If search returns no results, try adding science-related or technical/location terms (e.g., magnetic, reconnection, magnetopause, dayside, nightside, plasma sheet). Press Enter to search, Shift+Enter for new line."
                    />
                    {query && !loading && (
                        <button
                            className="timestamp-clear-button"
                            onClick={() => setQuery('')}
                            title="Clear search query"
                            type="button"
                        >
                            ✕
                        </button>
                    )}
                </div>
                <button
                    className="timestamp-search-button"
                    onClick={handleSearch}
                    disabled={loading}
                >
                    {loading ? 'Searching...' : 'Search'}
                </button>
            </div>

            {timestampError && (
                <div className="timestamp-error-warning">
                    <div className="timestamp-error-title">⚠ Cannot Apply Timestamp</div>
                    <div className="timestamp-error-time">Date/time: {timestampError.timestamp}</div>
                    <div className="timestamp-error-note">{timestampError.message}</div>
                </div>
            )}

            {selectedTimestamp && (
                <div className="timestamp-success-message">
                    <div className="timestamp-success-title">✓ Applied to Calendar</div>
                    <div className="timestamp-success-time">Date/time: {selectedTimestamp}</div>
                    <div className="timestamp-success-note">(Note: Calendar may center this time in a 2-hour window)</div>
                </div>
            )}

            {error && (
                <div className="timestamp-error-message">
                    {error}
                </div>
            )}

            {loading && (
                <div className="timestamp-loading">
                    <div className="timestamp-spinner"></div>
                    <span>Searching database... ({elapsedSeconds}s)</span>
                </div>
            )}

            {!loading && searched && results.length === 0 && !error && (
                <div className="timestamp-no-results">
                    No results found for "{query}". Try different keywords.
                </div>
            )}

            {!loading && results.length > 0 && (
                <div className="timestamp-results-container">
                    <div className="timestamp-results-header">
                        Found {results.length} result{results.length !== 1 ? 's' : ''}
                    </div>
                    <div className="timestamp-results-list">
                        {results.map((result, index) => (
                            <div key={index} className="timestamp-result-item">
                                {/* Compact row: Title/DOI on left | Date button above Cited on right */}
                                <div 
                                    className={`timestamp-result-summary ${expandedEventIndex === index ? 'expanded' : ''}`}
                                    onClick={() => handleEventClick(index)}
                                >
                                    <div className="timestamp-paper-info">
                                        <div className="timestamp-paper-title-compact">{(result.source || 'Unknown Source').replace(/\.txt$/i, '')}</div>
                                        {result.doi && (
                                            <a
                                                href={`https://doi.org/${result.doi}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="timestamp-doi-link-compact"
                                                onClick={(e) => e.stopPropagation()} // Prevent row click
                                            >
                                                {result.doi}
                                            </a>
                                        )}
                                    </div>
                                    
                                    <div className="timestamp-result-sidebar">
                                        <span
                                            className="timestamp-date-button"
                                            onClick={(e) => {
                                                e.stopPropagation(); // Prevent row click
                                                handleEventClick(index);
                                            }}
                                            title="Click to view event details"
                                        >
                                            {result.timestamp}
                                        </span>
                                        {result.citedBy !== null && result.citedBy !== undefined && (
                                            <div className="timestamp-cited-compact">
                                                Cited: {result.citedBy}
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="timestamp-expand-icon">
                                        {expandedEventIndex === index ? '▼' : '▶'}
                                    </div>
                                </div>

                                {/* Expandable detail panel */}
                                {expandedEventIndex === index && (() => {
                                    // Get all timestamps from this paper
                                    const allTimestamps = result.allTimestamps || [];
                                    
                                    // Determine how many subevents to show initially (limit for "More" button)
                                    const maxInitialSubevents = 3;
                                    const hasMoreTimestamps = allTimestamps.length > maxInitialSubevents;
                                    const visibleSubevents = showAllTimestamps ? allTimestamps : allTimestamps.slice(0, maxInitialSubevents);

                                    return (
                                        <div className="timestamp-detail-panel">
                                            {/* Event Description */}
                                            <div className="timestamp-detail-event">
                                                <span className="timestamp-detail-event-label">Event Description: </span>
                                                {result.event || 'No event description available'}
                                            </div>

                                            {/* Figure Caption (if available) */}
                                            {result.caption && (
                                                <div className="timestamp-detail-caption">
                                                    <span className="timestamp-detail-caption-label">Figure Caption: </span>
                                                    {result.caption}
                                                </div>
                                            )}

                                            {/* Abstract */}
                                            {result.abstract && (
                                                <div className="timestamp-detail-abstract">
                                                    <span className="timestamp-detail-abstract-label">Abstract: </span>
                                                    {result.abstract}
                                                </div>
                                            )}

                                            {/* Two-column layout: Main event info on left, Other timestamps on right */}
                                            <div className="timestamp-detail-columns">
                                                {/* Left column: Main event info */}
                                                <div className="timestamp-detail-left-column">
                                                    <div className="timestamp-apply-event-section">
                                                        <div className="timestamp-apply-event-label">
                                                            Apply Event Date/Time:
                                                        </div>
                                                        <div className="timestamp-subevent-row">
                                                            <span className="timestamp-subevent-time">
                                                                {result.timestamp}
                                                            </span>
                                                            <button
                                                                className="timestamp-subevent-apply-button"
                                                                onClick={() => handleApplyTimestampToCalendar(result.timestamp)}
                                                            >
                                                                Apply
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Right column: Other event times */}
                                                {allTimestamps && allTimestamps.length > 0 && (
                                                    <div className="timestamp-detail-right-column">
                                                        <div className="timestamp-subevents-section">
                                                            <div className="timestamp-subevents-label">
                                                                Other Event Times Mentioned in Paper:
                                                            </div>
                                                            <div className="timestamp-subevents-list">
                                                                {visibleSubevents.map((subevent, subIdx) => {
                                                                    const globalIndex = allTimestamps.indexOf(subevent);
                                                                    const isSubeventExpanded = expandedSubeventIndex === globalIndex;
                                                                    const hasDetails = subevent.event_description || subevent.caption_text;
                                                                    
                                                                    return (
                                                                        <div key={globalIndex} className="timestamp-subevent-item">
                                                                            <div className="timestamp-subevent-row">
                                                                                <span 
                                                                                    className={`timestamp-subevent-time ${hasDetails ? 'clickable' : ''}`}
                                                                                    onClick={hasDetails ? () => handleSubeventClick(globalIndex) : undefined}
                                                                                    title={hasDetails ? "Click to view event details" : ""}
                                                                                >
                                                                                    {subevent.datetime}
                                                                                    {hasDetails && (
                                                                                        <span className="timestamp-subevent-expand-indicator">
                                                                                            {isSubeventExpanded ? ' ▼' : ' ▶'}
                                                                                        </span>
                                                                                    )}
                                                                                </span>
                                                                                <button
                                                                                    className="timestamp-subevent-apply-button"
                                                                                    onClick={() => handleApplyTimestampToCalendar(subevent.datetime)}
                                                                                >
                                                                                    Apply
                                                                                </button>
                                                                            </div>
                                                                            
                                                                            {isSubeventExpanded && hasDetails && (
                                                                                <div className="timestamp-subevent-details">
                                                                                    {subevent.event_description && (
                                                                                        <div className="timestamp-subevent-event">
                                                                                            <span className="timestamp-subevent-label">Event: </span>
                                                                                            {subevent.event_description}
                                                                                        </div>
                                                                                    )}
                                                                                    {subevent.caption_text && (
                                                                                        <div className="timestamp-subevent-caption">
                                                                                            <span className="timestamp-subevent-label">Caption: </span>
                                                                                            {subevent.caption_text}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                                {hasMoreTimestamps && !showAllTimestamps && (
                                                                    <button
                                                                        className="timestamp-show-more-button"
                                                                        onClick={() => setShowAllTimestamps(true)}
                                                                    >
                                                                        More ▼
                                                                    </button>
                                                                )}
                                                                {showAllTimestamps && hasMoreTimestamps && (
                                                                    <button
                                                                        className="timestamp-show-more-button"
                                                                        onClick={() => setShowAllTimestamps(false)}
                                                                    >
                                                                        Less ▲
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SciencePaperSearch;
